import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { UI_ICON_SPRITE_BASE } from './icon.config';
import { groupForIcon, type IconGroup } from './icon-names';
import { resolveIconName, type IconInput } from './icon-aliases';

export type { IconName, IconGroup, InterfaceIconName } from './icon-names';
export { ICON_ALIASES, resolveIconName, type IconAlias, type IconInput } from './icon-aliases';
export { UI_ICON_SPRITE_BASE, provideUiIcons } from './icon.config';

/**
 * Groups whose artwork is solid rather than stroked, so the colour arrives as a
 * fill. The sprite generator strips their baked fills for exactly this reason —
 * see MONOCHROME_VENDOR_GROUPS in scripts/generate-icon-sprites.mjs.
 */
const FILLED_GROUPS = new Set<IconGroup>(['extras', 'socials']);

/**
 * Renders an icon from the generated SVG sprites.
 *
 * Sprites are produced by `npm run icons` (see
 * `scripts/generate-icon-sprites.mjs`) — one file per group, so the 200+ flags
 * are only fetched by screens that use them rather than riding along in every
 * bundle.
 *
 * Icons inherit `currentColor`, so they follow the text colour of wherever they
 * sit; size comes from `--icon-size` and stroke weight from the `strokeWidth`
 * input (the design library specifies 1.5 and 2).
 *
 * The interface set is drawn with strokes and the socials set with solid shapes,
 * so the colour has to arrive as `stroke` for one and `fill` for the other. That
 * follows from the icon's group rather than being asked for — a caller writing
 * `<ui-icon name="social-linkedin" />` should not have to know which.
 *
 * Accessibility: an icon is decorative by default and hidden from assistive
 * tech. Pass `label` when the icon carries meaning on its own — an icon-only
 * button, for instance — and it becomes `role="img"` with that accessible name.
 *
 * ```html
 * <ui-icon name="bell" />
 * <ui-icon name="close" label="Close dialog" />
 * <ui-icon name="chevron-down" [strokeWidth]="1.5" />
 * ```
 */
@Component({
  selector: 'ui-icon',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: inline-flex;
      flex: none;
    }

    svg {
      width: var(--icon-size, 20px);
      height: var(--icon-size, 20px);
    }
  `,
  template: `
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      [attr.fill]="filled() ? 'currentColor' : 'none'"
      [attr.stroke]="filled() ? null : 'currentColor'"
      [attr.stroke-width]="filled() ? null : strokeWidth()"
      [attr.stroke-linecap]="filled() ? null : 'round'"
      [attr.stroke-linejoin]="filled() ? null : 'round'"
      [attr.role]="label() ? 'img' : null"
      [attr.aria-label]="label()"
      [attr.aria-hidden]="label() ? null : 'true'"
    >
      @if (label()) {
        <svg:title>{{ label() }}</svg:title>
      }
      <svg:use [attr.href]="href()" />
    </svg>
  `,
})
export class Icon {
  /** Sprite icon name, or one of the semantic aliases in `ICON_ALIASES`. */
  readonly name = input.required<IconInput>();

  /** Accessible name. Omit for decorative icons — they are then `aria-hidden`. */
  readonly label = input<string>();

  /** Stroke weight; the design library uses 1.5 and 2. */
  readonly strokeWidth = input(2);

  private readonly spriteBase = inject(UI_ICON_SPRITE_BASE);

  protected readonly filled = computed(() => FILLED_GROUPS.has(this.group()));

  private readonly group = computed<IconGroup>(() => {
    const resolved = resolveIconName(this.name());
    const group = groupForIcon(resolved);

    if (!group) {
      // A name outside the manifest cannot resolve to a symbol. Fail loudly in
      // development rather than rendering an invisible empty <use>.
      throw new Error(
        `<ui-icon>: unknown icon "${resolved}". Add it to the sprite set and run \`npm run icons\`.`,
      );
    }

    return group;
  });

  protected readonly href = computed(
    () => `${this.spriteBase}/${this.group()}.svg#${resolveIconName(this.name())}`,
  );
}
